import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, TrendingUp, Users, Target } from 'lucide-react';
interface Slide3Props {
  isDark?: boolean;
}
export function Slide3({
  isDark = false
}: Slide3Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-835">
      <div className="min-h-full flex flex-col justify-start md:justify-center max-w-5xl mx-auto px-6 sm:px-12 py-20 sm:py-24" data-id="element-836">
        <motion.div variants={itemVariants} className="mb-12 sm:mb-16 text-center" data-id="element-837">
          <h1 className={`text-3xl sm:text-5xl font-serif font-medium mb-5 sm:mb-6 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-838">
            How We Measure Pilot Success
          </h1>
          <p className={`text-base sm:text-xl max-w-3xl mx-auto px-4 ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-839">
            Three clear indicators validate the value equation within 60–90 days
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 mb-10 sm:mb-14" data-id="element-840">
          {/* Metric 1 */}
          <motion.div variants={itemVariants} className="text-center" data-id="element-841">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-heliona-orange/10 flex items-center justify-center mb-5 sm:mb-6 mx-auto border border-heliona-orange/30 shadow-sm" data-id="element-842">
              <TrendingUp className="text-heliona-orange w-8 h-8 sm:w-10 sm:h-10" data-id="element-843" />
            </div>
            <h3 className={`text-lg sm:text-2xl font-bold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-844">
              Leadership Health
            </h3>
            <p className={`leading-relaxed text-sm sm:text-base px-2 ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-845">
              Helium Score improvement across clarity, communication, capacity,
              and crisis readiness
            </p>
          </motion.div>

          {/* Metric 2 */}
          <motion.div variants={itemVariants} className="text-center" data-id="element-846">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-heliona-mint/10 flex items-center justify-center mb-5 sm:mb-6 mx-auto border border-heliona-mint/30 shadow-sm" data-id="element-847">
              <Users className={`w-8 h-8 sm:w-10 sm:h-10 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-848" />
            </div>
            <h3 className={`text-lg sm:text-2xl font-bold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-849">
              Engagement
            </h3>
            <p className={`leading-relaxed text-sm sm:text-base px-2 ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-850">
              Active use of coaching, care resources, and personalized learning
              paths
            </p>
          </motion.div>

          {/* Metric 3 */}
          <motion.div variants={itemVariants} className="text-center" data-id="element-851">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-heliona-navy/10 flex items-center justify-center mb-5 sm:mb-6 mx-auto border border-heliona-navy/30 shadow-sm" data-id="element-852">
              <Target className="text-heliona-navy w-8 h-8 sm:w-10 sm:h-10" data-id="element-853" />
            </div>
            <h3 className={`text-lg sm:text-2xl font-bold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-854">
              Team Impact
            </h3>
            <p className={`leading-relaxed text-sm sm:text-base px-2 ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-855">
              Improved climate scores and positive feedback from leaders and
              executives
            </p>
          </motion.div>
        </div>

        <motion.div variants={itemVariants} className={`rounded-2xl p-6 sm:p-8 flex items-center gap-4 sm:gap-6 max-w-4xl mx-auto mb-16 sm:mb-0 shadow-sm ${isDark ? 'bg-heliona-mint/10 border border-heliona-mint/30' : 'bg-gradient-to-br from-heliona-mint/10 to-heliona-mint/5 border border-heliona-mint/30'}`} data-id="element-856">
          <div className="p-3 sm:p-4 rounded-xl bg-heliona-mint/20 border border-heliona-mint/40 flex-shrink-0" data-id="element-857">
            <CheckCircle2 className={`w-8 h-8 sm:w-12 sm:h-12 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-858" />
          </div>
          <div data-id="element-859">
            <p className={`text-base sm:text-xl font-medium leading-relaxed ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-860">
              Measurable improvement in any two areas validates the investment
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}